/**
 * Web OAuth callback. detectSessionInUrl owns the PKCE exchange for
 * https /auth/callback?code=... — this page must not call exchangeCodeForSession.
 * Native custom-scheme exchange stays in src/main.tsx.
 *
 * HOTFIX: Coordinate the web callback with Supabase client initialization while
 * preserving the existing branded loading screen and native callback path.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// V333.3: Use same client as useAuth to prevent auth loop (session stored/read by single instance)
import { supabase } from '../integrations/supabase/client';
import { processGoogleProfileImage } from '../utils/imageProcessing';
import { logOAuthCallbackReceived, logSupabaseSignInResult } from '../utils/authDiagLogger';
import { Capacitor } from '@capacitor/core';
import { useTranslation } from 'react-i18next';
import { BlissNetLoadingScreen } from '@/components/BlissNetLoadingScreen';

const DEV = import.meta.env.DEV;

type CallbackUiMode = 'loading' | 'slow' | 'error';

const AuthCallback: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Internal step tracking only — not bound to normal loading copy
  const [, setStatus] = useState('Completing sign in...');
  const [uiMode, setUiMode] = useState<CallbackUiMode>('loading');
  const [safeError, setSafeError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  const completedRef = useRef(false);
  const slowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorRedirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackStartedRef = useRef(false);

  const clearSlowTimer = useCallback(() => {
    if (slowTimerRef.current != null) {
      clearTimeout(slowTimerRef.current);
      slowTimerRef.current = null;
    }
  }, []);

  const clearErrorRedirectTimer = useCallback(() => {
    if (errorRedirectTimerRef.current != null) {
      clearTimeout(errorRedirectTimerRef.current);
      errorRedirectTimerRef.current = null;
    }
  }, []);

  const markFlowSettled = useCallback(() => {
    completedRef.current = true;
    clearSlowTimer();
    clearErrorRedirectTimer();
  }, [clearSlowTimer, clearErrorRedirectTimer]);

  const showSafeError = useCallback((message: string) => {
    if (!mountedRef.current) return;
    markFlowSettled();
    setSafeError(message);
    setUiMode('error');
    setStatus(message);
  }, [markFlowSettled]);

  const returnToSignIn = () => {
    markFlowSettled();
    navigate('/auth', { replace: true });
  };

  // DEV-only diagnostics — never rendered in JSX
  const [diag] = useState(() => {
    const url = new URL(window.location.href);
    const searchParams = new URLSearchParams(url.search);
    const hashParams = new URLSearchParams(window.location.hash?.replace(/^#/, '') || '');
    const keySet = new Set<string>();
    searchParams.forEach((_, key) => keySet.add(key));
    hashParams.forEach((_, key) => keySet.add(key));

    return {
      origin: url.origin,
      path: url.pathname,
      hasCode: searchParams.has('code') || hashParams.has('code'),
      hasError:
        searchParams.has('error') ||
        searchParams.has('error_description') ||
        hashParams.has('error') ||
        hashParams.has('error_description'),
      paramKeys: Array.from(keySet),
      isNative: Capacitor.isNativePlatform(),
      platform: Capacitor.getPlatform(),
    };
  });

  // Dismiss HTML boot loader + start non-blocking 10s slow-state timer
  useEffect(() => {
    mountedRef.current = true;

    const loader = document.getElementById('initial-loader');
    if (loader) {
      if (loader.parentNode) {
        loader.parentNode.removeChild(loader);
      } else {
        loader.style.display = 'none';
      }
    }

    slowTimerRef.current = setTimeout(() => {
      if (mountedRef.current && !completedRef.current) {
        setUiMode('slow');
      }
    }, 10_000);

    if (DEV) {
      console.debug('[AUTHFLOW] web fallback callback reached', {
        origin: diag.origin,
        path: diag.path,
        hasCode: diag.hasCode,
        hasError: diag.hasError,
        paramKeys: diag.paramKeys,
        isNative: diag.isNative,
        platform: diag.platform,
      });
    }

    return () => {
      mountedRef.current = false;
      clearSlowTimer();
      clearErrorRedirectTimer();
    };
  }, [diag, clearSlowTimer, clearErrorRedirectTimer]);

  useEffect(() => {
    if (callbackStartedRef.current) return;
    callbackStartedRef.current = true;

    const handleAuthCallback = async () => {
      try {
        // PKCE query (?code=) is exchanged by detectSessionInUrl during client init.
        const searchParams = new URLSearchParams(window.location.search);
        const code = searchParams.get('code');
        const hashParams = new URLSearchParams(window.location.hash?.replace(/^#/, '') || '');
        const codeFromHash = hashParams.get('code');
        const finalCode = code || codeFromHash;

        if (DEV) {
          logOAuthCallbackReceived(window.location.href, !!finalCode, hashParams.has('id_token'), {});
        }

        setStatus('Verifying session...');
        const { data: { session } } = await supabase.auth.getSession();
        if (!mountedRef.current) return;

        if (!session) {
          if (!finalCode) {
            if (DEV) console.warn('[AuthCallback] No code in URL, no session');
            logSupabaseSignInResult(false, 'no_code');
            markFlowSettled();
            navigate('/auth?error=no_code', { replace: true });
            return;
          }
          logSupabaseSignInResult(false, 'no_session_after_initialize');
          await rejectUnlessSession(t('authCallback.authenticationFailed'), '/auth?error=callback_failed');
          return;
        }

        logSupabaseSignInResult(true);

        // Client init already removes ?code= after a successful exchange. Strip a leftover code.
        if (finalCode) {
          window.history.replaceState({}, '', window.location.pathname);
        }

        setStatus('Checking profile...');

        // Poll for user record (new users may have a brief delay)
        // V334.3: Also fetch photo_urls + profile_image_public_id to guard against overwrite
        // V334.4: Capture errors — fail-closed (skip import if DB lookup fails)
        let userExists = false;
        let existingPhotoUrls: string[] | null = null;
        let existingPublicId: string | null = null;
        let dbLookupFailed = false;
        for (let i = 0; i < 6; i++) {
          const { data: userRecord, error: userErr } = await supabase
            .from('users')
            .select('id, photo_urls, profile_image_public_id')
            .eq('id', session.user.id)
            .maybeSingle();
          if (userErr) {
            // V334.4: Fail-closed — assume user exists with photo; never import on error
            if (DEV) console.log('[V334.4][photoImport] user fetch error; skipping import (fail-closed)', userErr.message);
            userExists = true;
            dbLookupFailed = true;
            break;
          }
          if (userRecord) {
            userExists = true;
            existingPhotoUrls = userRecord.photo_urls;
            existingPublicId = userRecord.profile_image_public_id;
            break;
          }
          await new Promise(r => setTimeout(r, 500));
        }

        if (!userExists) {
          setStatus('Setting up your account...');
          markFlowSettled();
          navigate('/onboarding', { replace: true });
          return;
        }

        // V334.3: DB-truth guard — never overwrite existing Cloudinary photo on login
        // V334.4: Also true when DB lookup failed (fail-closed)
        // V334.4b: Only treat Cloudinary URLs as "real photo" (not stale random URLs)
        const isRealPhotoUrl = (url: unknown): boolean =>
          typeof url === 'string' &&
          (url.includes('res.cloudinary.com/dj3fiu3rq/') || url.includes('cloudinary.com'));

        const hasExistingPhoto =
          dbLookupFailed ||
          (typeof existingPublicId === 'string' && existingPublicId.trim().length > 0) ||
          (Array.isArray(existingPhotoUrls) && existingPhotoUrls.some(isRealPhotoUrl));

        // Process Google image ONLY on first signup (no existing photo in DB)
        const googleImageUrl = session.user.user_metadata?.avatar_url;
        if (googleImageUrl?.includes('googleusercontent.com') && !hasExistingPhoto) {
          if (DEV) console.log('[V334.4][photoImport] running: no existing photo, importing google avatar');
          try {
            setStatus('Optimizing your profile photo...');
            const processedUrl = await processGoogleProfileImage(
              googleImageUrl,
              session.user.id,
              supabase
            );
            const urlToUse = processedUrl || googleImageUrl;
            // V334.4: Removed manual updated_at — DB trigger owns timestamps
            const { error: updErr } = await supabase
              .from('users')
              .update({ photo_urls: [urlToUse] })
              .eq('id', session.user.id);
            if (updErr && DEV) console.log('[V334.4][photoImport] update failed', updErr.message);
          } catch {
            // V334.4: Removed manual updated_at — DB trigger owns timestamps
            const { error: updErr2 } = await supabase
              .from('users')
              .update({ photo_urls: [googleImageUrl] })
              .eq('id', session.user.id);
            if (updErr2 && DEV) console.log('[V334.4][photoImport] fallback update failed', updErr2.message);
          }
        } else if (hasExistingPhoto) {
          if (DEV) console.log('[V334.4][photoImport] skipped: existing photo or db lookup uncertain');
        }

        await navigateToDestination(session);
      } catch (err) {
        if (DEV) console.error('[AuthCallback] Exception:', err);
        logSupabaseSignInResult(false, err instanceof Error ? err.message : String(err));
        await rejectUnlessSession(t('authCallback.authenticationError'), '/auth?error=exception');
      }
    };

    async function rejectUnlessSession(message: string, errorSearch: string) {
      const { data: { session } } = await supabase.auth.getSession();
      if (!mountedRef.current) return;
      if (session) {
        logSupabaseSignInResult(true);
        await navigateToDestination(session);
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 400));

      if (!mountedRef.current || completedRef.current) return;

      const { data: { session: settledSession } } = await supabase.auth.getSession();
      if (!mountedRef.current || completedRef.current) return;
      if (settledSession) {
        logSupabaseSignInResult(true);
        await navigateToDestination(settledSession);
        return;
      }

      showSafeError(message);
      errorRedirectTimerRef.current = setTimeout(() => {
        errorRedirectTimerRef.current = null;
        if (!mountedRef.current) return;
        navigate(errorSearch, { replace: true });
      }, 1500);
    }

    async function navigateToDestination(session: { user: { id: string } }) {
      setSafeError(null);
      setUiMode('loading');
      markFlowSettled();
      // Respect redirect param (e.g. /auth?redirect=/bar-ilan/dashboard)
      const redirect = sessionStorage.getItem('authRedirect');
      if (redirect && redirect.startsWith('/')) {
        sessionStorage.removeItem('authRedirect');
        navigate(redirect, { replace: true });
        return;
      }
      // V334.X: Default to /profile (Trust Score, profile completion)
      navigate('/profile', { replace: true });
    }

    handleAuthCallback();
  }, [navigate, markFlowSettled, showSafeError, t]);

  const secondaryText =
    uiMode === 'error' && safeError
      ? safeError
      : uiMode === 'slow'
        ? t('authCallback.takingLonger')
        : t('authCallback.checkingProfile');

  return (
    <BlissNetLoadingScreen
      title={t('authCallback.completingSignIn')}
      message={secondaryText}
      mode={uiMode}
      loadingLabel={t('authCallback.loading')}
      actions={
        uiMode === 'slow' ? (
          <>
            <button
              type="button"
              onClick={returnToSignIn}
              className="w-full rounded-xl bg-gradient-to-r from-pink-500 to-violet-600 px-5 py-3.5 text-base font-semibold text-white shadow-[0_0_24px_rgba(236,72,153,0.35)] transition hover:from-pink-400 hover:to-violet-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#050508]"
            >
              {t('authCallback.tryAgain')}
            </button>
            <button
              type="button"
              onClick={returnToSignIn}
              className="w-full rounded-xl border border-white/20 bg-white/5 px-5 py-3.5 text-base font-medium text-white backdrop-blur-sm transition hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#050508]"
            >
              {t('authCallback.returnToSignIn')}
            </button>
          </>
        ) : undefined
      }
    />
  );
};

export default AuthCallback;
